import React, { forwardRef, InputHTMLAttributes, useId } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> {
  label: string;
  description?: string;
  error?: string;
  required?: boolean;
  onChange?: (checked: boolean) => void;
  containerClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      required,
      onChange,
      containerClassName = '',
      className = '',
      id,
      checked,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = id || generatedId;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    return (
      <div className={`mb-4 ${containerClassName}`}>
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              checked={checked}
              onChange={handleChange}
              disabled={disabled}
              className={`
                w-5 h-5
                border-2 rounded
                text-green-600
                focus:ring-2 focus:ring-green-200 focus:ring-offset-0
                transition-all duration-200
                cursor-pointer
                disabled:cursor-not-allowed disabled:opacity-50
                ${error ? 'border-red-500' : 'border-gray-300 hover:border-green-500'}
                ${className}
              `}
              aria-invalid={!!error}
              aria-required={required}
              aria-describedby={
                error
                  ? `${checkboxId}-error`
                  : description
                  ? `${checkboxId}-description`
                  : undefined
              }
              {...props}
            />
          </div>
          <div className="ml-3">
            <label
              htmlFor={checkboxId}
              className={`
                text-sm font-medium cursor-pointer
                ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}
              `}
            >
              {label}
            </label>
            {description && (
              <p
                id={`${checkboxId}-description`}
                className="text-sm text-gray-500 mt-0.5"
              >
                {description}
              </p>
            )}
          </div>
        </div>

        {error && (
          <p id={`${checkboxId}-error`} className="mt-1 text-sm text-red-600 ml-8" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
